import type { AutomationProjectDto } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeftRight, RefreshCw, Trash2, Upload, UserPlus, Users } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  Field,
  Input,
  SelectMenu,
  SelectMenuItem,
  SkeletonRows,
  Table,
  TableWrap,
  Td,
  Th,
} from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import { useAutomationTeams } from '../api';
import { useAutomationCan, useStartRun } from '../hooks';
import type { RegistrationKind, RegistrationStatus } from '../testrun-types';
import {
  useCheckRegistration,
  useDeleteRegistration,
  useRegistrationList,
  useReleaseRegistration,
  useSettleRegistration,
} from './api';
import { normalizePhone, onlyDigits, REG_CLASS, REG_KINDS, REG_STATUSES } from './format';
import { RegistrationImportPanel } from './ImportPanels';
import { Fail, Notice, useConfirm, useReadableError, useTestRunErrorText } from './shared';

/**
 * Registering a new client. TMS does not register by itself: it runs the
 * proven authenticate_api_testing class (RegistrationIe, RegistrationLlp,
 * RegistrationLlpMultiFounder, RegistrationSimplifiedAccess) with the account
 * swapped for that class (-Dauth.phone.<Class>…). The progress is the usual run
 * page. Accounts come from «for registration» (TestRun server/src/registration.mjs):
 * the pool holds registered clients only, registration needs one the app does not know.
 */

/** freedom-business/qa/autotests/qa/authenticate_api_testing */
const REGISTRATION_GITLAB_ID = '3104';

const STATUS_STYLE: Record<RegistrationStatus, string> = {
  free: 'bg-pass-soft text-pass',
  issued: 'bg-accent-soft text-accent-fg',
  registered: 'bg-skip-soft text-skip',
  used: 'bg-block-soft text-block',
  unsuitable: 'bg-fail-soft text-fail',
};

const ALL_STATUSES = 'all';

const isRegistrationProject = (p: AutomationProjectDto) => {
  const path = p.sourceProject?.['path'];
  return (
    String(p.gitlabProjectId) === REGISTRATION_GITLAB_ID ||
    (typeof path === 'string' && /authenticate_api_testing$/.test(path))
  );
};

/** The «for registration» list: unregistered clients, who they were issued to and how it ended. */
const RegistrationList = () => {
  const { t } = useT();
  const { relative } = useFormat();
  const can = useAutomationCan();
  const readable = useReadableError();
  const errorText = useTestRunErrorText();
  const confirm = useConfirm();
  const list = useRegistrationList();
  const settle = useSettleRegistration();
  const check = useCheckRegistration();
  const release = useReleaseRegistration();
  const remove = useDeleteRegistration();
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState<string>('free');
  const issued = list.data?.accounts.filter((a) => a.status === 'issued').length ?? 0;
  const failed = (error: unknown) =>
    toast.error(t('automation.accounts.act.failed'), { description: errorText(error) });

  const header = (
    <CardHeader
      title={
        <span className="flex items-center gap-2">
          <Users className="size-4" aria-hidden="true" />
          {t('automation.accounts.reg.listTitle')}
        </span>
      }
      description={t('automation.accounts.reg.listHint')}
      action={
        can.run && list.data ? (
          <div className="flex shrink-0 gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setImporting((v) => !v)}
              aria-expanded={importing}
            >
              <Upload className="size-3.5" aria-hidden="true" />
              {t('automation.accounts.import.button')}
            </Button>
            <Button
              size="sm"
              onClick={() => settle.mutate(undefined, { onError: failed })}
              disabled={!issued}
              loading={settle.isPending}
              title={t('automation.accounts.reg.settleHint')}
            >
              {settle.isPending ? null : <RefreshCw className="size-3.5" aria-hidden="true" />}
              {issued
                ? t('automation.accounts.reg.settleCount', { count: issued })
                : t('automation.accounts.reg.settle')}
            </Button>
          </div>
        ) : null
      }
    />
  );

  if (list.error) {
    return (
      <Card>
        {header}
        <ErrorState error={readable(list.error)} onRetry={() => void list.refetch()} />
      </Card>
    );
  }
  if (!list.data) {
    return (
      <Card>
        {header}
        <SkeletonRows rows={3} />
      </Card>
    );
  }

  const { accounts, counts } = list.data;
  const rows = accounts.filter((a) => status === ALL_STATUSES || a.status === status);

  return (
    <Card>
      {header}
      <div className="space-y-3 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {REG_KINDS.map((k) => {
            const c = counts[k] ?? {};
            return (
              <Badge key={k}>
                {t('automation.accounts.reg.counts', {
                  kind: t(`automation.accounts.reg.kind.${k}`),
                  free: c.free ?? 0,
                })}
                {c.issued
                  ? `, ${t('automation.accounts.reg.countIssued', { count: c.issued })}`
                  : ''}
                {c.registered
                  ? `, ${t('automation.accounts.reg.countRegistered', { count: c.registered })}`
                  : ''}
              </Badge>
            );
          })}
          <SelectMenu
            value={status}
            onValueChange={setStatus}
            aria-label={t('automation.accounts.reg.statusFilter')}
            className="ml-auto w-52"
          >
            <SelectMenuItem value={ALL_STATUSES}>
              {t('automation.accounts.reg.allStatuses')}
            </SelectMenuItem>
            {REG_STATUSES.map((s) => (
              <SelectMenuItem key={s} value={s}>
                {t(`automation.accounts.reg.status.${s}`)}
              </SelectMenuItem>
            ))}
          </SelectMenu>
        </div>

        {importing && can.run ? (
          <RegistrationImportPanel onClose={() => setImporting(false)} />
        ) : null}

        {!accounts.length ? (
          <p className="text-[13px] text-ink-muted">{t('automation.accounts.reg.empty')}</p>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>{t('automation.accounts.reg.kindColumn')}</Th>
                  <Th>{t('automation.accounts.reg.who')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('automation.accounts.reg.lastCheck')}</Th>
                  {can.run ? <Th aria-label={t('automation.accounts.actions')} /> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <Td className="whitespace-nowrap">
                      {t(`automation.accounts.reg.kind.${a.kind}`)}
                      {a.label ? <div className="text-[11px] text-ink-muted">{a.label}</div> : null}
                    </Td>
                    <Td className="font-mono text-[12px]">
                      {a.phone}
                      <div className="text-ink-muted">
                        {a.iin}
                        {a.bin ? ` / ${a.bin}` : ''}
                      </div>
                    </Td>
                    <Td>
                      <Badge
                        className={STATUS_STYLE[a.status]}
                        title={t(`automation.accounts.reg.statusHint.${a.status}`)}
                      >
                        {t(`automation.accounts.reg.status.${a.status}`)}
                      </Badge>
                      {a.issued && a.status === 'issued' ? (
                        <div className="text-[11px] text-ink-muted">
                          <Link
                            to="/automation/runs/$runId"
                            params={{ runId: a.issued.runId.replace('#', '') }}
                            search={{ project: a.issued.projectId }}
                            className="text-accent-fg hover:underline"
                          >
                            #{a.issued.runId.replace('#', '')}
                          </Link>{' '}
                          · {a.issued.class}
                        </div>
                      ) : null}
                      {a.note ? (
                        <div className="max-w-80 text-[11px] text-ink-muted">{a.note}</div>
                      ) : null}
                      {a.poolId ? (
                        <div className="text-[11px]">
                          <Link
                            to="/automation/accounts/$accountId"
                            params={{ accountId: a.poolId }}
                            className="text-accent-fg hover:underline"
                          >
                            {t('automation.accounts.reg.inPool')}
                          </Link>
                        </div>
                      ) : null}
                      {a.poolError ? (
                        <div className="text-[11px] text-fail">
                          {t('automation.accounts.reg.poolError', { error: a.poolError })}
                        </div>
                      ) : null}
                    </Td>
                    <Td className="text-[12px]">
                      {a.check ? (
                        <>
                          {relative(a.check.at)}
                          <div className="text-ink-muted">
                            {a.check.verified === true
                              ? t('automation.accounts.reg.verifiedYes')
                              : a.check.verified === false
                                ? t('automation.accounts.reg.verifiedNo')
                                : `${a.check.status} ${a.check.code ?? ''}`}
                          </div>
                        </>
                      ) : (
                        <span className="text-ink-muted">{t('common.empty')}</span>
                      )}
                    </Td>
                    {can.run ? (
                      <Td className="whitespace-nowrap text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={check.isPending}
                          onClick={() => check.mutate(a.id, { onError: failed })}
                          title={t('automation.accounts.reg.checkHint')}
                        >
                          <RefreshCw className="size-3.5" aria-hidden="true" />
                          {t('automation.accounts.reg.check')}
                        </Button>
                        {a.status !== 'free' && a.status !== 'registered' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={release.isPending}
                            onClick={() => release.mutate(a.id, { onError: failed })}
                            title={t('automation.accounts.reg.releaseHint')}
                          >
                            <ArrowLeftRight className="size-3.5" aria-hidden="true" />
                            {t('automation.accounts.reg.release')}
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={remove.isPending}
                          aria-label={t('automation.accounts.reg.removeLabel', { phone: a.phone })}
                          title={t('automation.accounts.reg.removeLabel', { phone: a.phone })}
                          onClick={() =>
                            confirm.ask({
                              title: t('automation.accounts.reg.removeConfirm', { phone: a.phone }),
                              description: t('automation.accounts.reg.removeHint'),
                              confirmLabel: t('automation.accounts.reg.remove'),
                              destructive: true,
                              onConfirm: () => remove.mutate(a.id, { onError: failed }),
                            })
                          }
                        >
                          <Trash2 className="size-3.5" aria-hidden="true" />
                        </Button>
                      </Td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </Table>
            {!rows.length ? (
              <p className="px-3 py-3 text-[13px] text-ink-muted">
                {t('automation.accounts.reg.noneWithStatus')}
              </p>
            ) : null}
          </TableWrap>
        )}
      </div>
      {confirm.dialog}
    </Card>
  );
};

/** The form: an account for the chosen kind, and a run of its registration class. */
const RegistrationForm = ({ project }: { project: AutomationProjectDto }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const errorText = useTestRunErrorText();
  const confirm = useConfirm();
  const list = useRegistrationList();
  const start = useStartRun(project.id);
  const [kind, setKind] = useState<RegistrationKind>('IP');
  const [form, setForm] = useState({ phone: '', iin: '', bin: '' });
  const className = REG_CLASS[kind];
  const freeOf = (k: RegistrationKind) =>
    (list.data?.accounts ?? [])
      .filter((a) => a.status === 'free' && a.kind === k)
      .sort((x, y) => x.attempts - y.attempts || x.addedAt.localeCompare(y.addedAt));
  const takeNext = () => {
    const next = freeOf(kind)[0];
    if (next) setForm({ phone: next.phone, iin: next.iin, bin: next.bin ?? '' });
  };

  const launch = (input: { phone: string; iin: string; bin?: string }) => {
    // an account from the list is marked as issued to this run; TestRun settles it after the run
    const fromList = (list.data?.accounts ?? []).find(
      (a) => a.status === 'free' && a.phone === input.phone && a.iin === input.iin,
    );
    start.mutate(
      {
        type: 'classes',
        classes: [className],
        testType: 'API',
        environment: 'STAGE',
        gradleTask: 'stage_registration',
        accountOverrides: [{ className, ...input }],
        ...(fromList ? { issueRegistration: [{ className, accountId: fromList.id }] } : {}),
      },
      {
        onSuccess: ({ run, marksError }) => {
          if (marksError)
            toast.warning(t('automation.accounts.reg.marksError', { error: marksError }));
          void navigate({
            to: '/automation/runs/$runId',
            params: { runId: run.pipelineId },
            search: { project: project.id },
          });
        },
      },
    );
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const phone = normalizePhone(form.phone);
    const iin = onlyDigits(form.iin);
    const bin = kind !== 'IP' ? onlyDigits(form.bin) : '';
    setForm((f) => ({ ...f, phone }));
    confirm.ask({
      title: t('automation.accounts.reg.confirm', {
        kind: t(`automation.accounts.reg.kind.${kind}`),
        who: `${phone}, ${t('automation.accounts.iin')} ${iin}${bin ? `, ${t('automation.accounts.bin')} ${bin}` : ''}`,
      }),
      description: t('automation.accounts.reg.confirmHint'),
      confirmLabel: t('automation.accounts.reg.submit'),
      onConfirm: () => launch({ phone, iin, ...(bin ? { bin } : {}) }),
    });
  };

  return (
    <Card>
      <form onSubmit={submit} className="space-y-4 px-4 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div
            role="radiogroup"
            aria-label={t('automation.accounts.reg.kindColumn')}
            className="flex flex-wrap gap-2"
          >
            {REG_KINDS.map((k) => (
              <label
                key={k}
                className={
                  kind === k
                    ? 'flex cursor-pointer items-center gap-1.5 rounded-md border border-accent bg-accent-soft px-2.5 py-1 text-[13px] text-ink'
                    : 'flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-[13px] text-ink-muted hover:bg-sunken'
                }
              >
                <input
                  type="radio"
                  name="reg-kind"
                  checked={kind === k}
                  onChange={() => setKind(k)}
                />
                {t(`automation.accounts.reg.kind.${k}`)}
                <span className="text-[11px] text-ink-subtle">
                  · {t('automation.accounts.reg.free', { count: freeOf(k).length })}
                </span>
              </label>
            ))}
          </div>
          <Button
            size="sm"
            onClick={takeNext}
            disabled={!freeOf(kind).length}
            title={t('automation.accounts.reg.nextFreeHint')}
          >
            <UserPlus className="size-3.5" aria-hidden="true" />
            {t('automation.accounts.reg.nextFree')}
          </Button>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field
            label={t('automation.accounts.phone')}
            hint={t('automation.accounts.reg.phoneHint')}
            required
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                placeholder={t('automation.accounts.phonePlaceholder')}
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                onBlur={() => setForm((f) => ({ ...f, phone: normalizePhone(f.phone) }))}
                inputMode="tel"
                autoComplete="off"
                required
              />
            )}
          </Field>
          <Field label={t('automation.accounts.iin')} required>
            {({ id }) => (
              <Input
                id={id}
                className="font-mono"
                placeholder={t('automation.accounts.digits12')}
                value={form.iin}
                onChange={(e) => setForm((f) => ({ ...f, iin: e.target.value }))}
                inputMode="numeric"
                autoComplete="off"
                required
              />
            )}
          </Field>
          {kind !== 'IP' ? (
            <Field label={t('automation.accounts.bin')} required>
              {({ id }) => (
                <Input
                  id={id}
                  className="font-mono"
                  placeholder={t('automation.accounts.digits12')}
                  value={form.bin}
                  onChange={(e) => setForm((f) => ({ ...f, bin: e.target.value }))}
                  inputMode="numeric"
                  autoComplete="off"
                  required
                />
              )}
            </Field>
          ) : null}
        </div>

        <Notice tone="info" title={t('automation.accounts.reg.needTitle')}>
          {t('automation.accounts.reg.need')} {t(`automation.accounts.reg.need.${kind}`)}{' '}
          {t('automation.accounts.reg.needTail', { className })}
        </Notice>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-[12px] text-ink-subtle">
            {t('automation.accounts.reg.project', { name: project.name })}
          </span>
          <Button type="submit" variant="primary" loading={start.isPending}>
            {start.isPending ? null : <UserPlus className="size-4" aria-hidden="true" />}
            {start.isPending
              ? t('automation.accounts.reg.starting')
              : t('automation.accounts.reg.submit')}
          </Button>
        </div>
        {start.isError ? <p className="text-[13px] text-fail">{errorText(start.error)}</p> : null}
      </form>
      {confirm.dialog}
    </Card>
  );
};

export const RegisterTab = () => {
  const { t } = useT();
  const can = useAutomationCan();
  const teams = useAutomationTeams();
  const project = (teams.data ?? []).flatMap((team) => team.projects).find(isRegistrationProject);

  return (
    <div className="space-y-4">
      {!can.run ? null : teams.isPending ? (
        <SkeletonRows rows={2} className="p-0" />
      ) : teams.error ? (
        <Fail title={t('automation.accounts.reg.projectsFailed')} error={teams.error} />
      ) : project ? (
        <RegistrationForm project={project} />
      ) : (
        <Notice tone="warn" title={t('automation.accounts.reg.noProject')}>
          {t('automation.accounts.reg.noProjectHint')}
        </Notice>
      )}
      <RegistrationList />
    </div>
  );
};
